/*
Policy for emitting client→server socket messages, extracted as pure functions
so the offline/retry behavior is unit-testable without a live socket.

Offline: never emit. A PerformAction is already durable in the unconfirmed-action
queue (SPAStorageAdapter.unconfirmedActions2) and is resent when the socket
reconnects and the client re-registers (see the socket "connect" handler →
Client.register()); Register/AcknowledgeActions are meaningless with the socket
down. This replaces the previous behavior, where an offline emit retried
forever — every ~5s — because the failed-emit callback re-invoked itself with no
cap and no online check.
*/

export const MAX_SOCKET_EMIT_ATTEMPTS = 3;

/** Whether to emit a message to the server now. Offline ⇒ never. */
export function shouldEmitToServer(isOnline: boolean): boolean {
  return isOnline;
}

/**
 * The next emit-attempt number after a failed attempt, or null once the cap is
 * reached — at which point we stop retrying and rely on the socket's
 * reconnect → register() path to resend any still-unconfirmed actions.
 */
export function nextSocketEmitAttempt(
  attempt: number,
  maxAttempts: number = MAX_SOCKET_EMIT_ATTEMPTS,
): number | null {
  return attempt < maxAttempts ? attempt + 1 : null;
}

export type EmitWithRetryResult = {
  /** False only when offline (the message was never put on the wire). */
  emitted: boolean;
  /** How many emit attempts were made (0 when offline). */
  attempts: number;
  /** Whether the server acknowledged the message within the attempt budget. */
  acked: boolean;
};

/**
 * Drive one client→server message through the offline gate and the bounded
 * emit-retry loop. `ClientWrapper.handleClientMessageToServer` delegates here,
 * so this captures the real wiring (no infinite loop, no offline emit) in a
 * single testable unit:
 *
 *  - Offline ⇒ `emitOnce` is never called; returns { emitted: false }. The
 *    action stays durable in the unconfirmed-action queue and resends on
 *    reconnect via register().
 *  - Online ⇒ call `emitOnce(attempt)`, which resolves to a falsy value on a
 *    server ack or a truthy error on failure/timeout. Retry up to `maxAttempts`,
 *    then stop (reconnect → register() resends anything still unconfirmed).
 *
 * A rejected `emitOnce` (a thrown emit) is NOT retried — the rejection
 * propagates to the caller, mirroring the previous behavior of surfacing a
 * thrown emit as a PerformActionFailed rather than looping on it.
 */
export async function runEmitWithRetry(
  isOnline: boolean,
  emitOnce: (attempt: number) => Promise<unknown>,
  maxAttempts: number = MAX_SOCKET_EMIT_ATTEMPTS,
): Promise<EmitWithRetryResult> {
  if (!shouldEmitToServer(isOnline)) {
    return { emitted: false, attempts: 0, acked: false };
  }
  let attempt = 1;
  for (;;) {
    const error = await emitOnce(attempt);
    if (!error) {
      return { emitted: true, attempts: attempt, acked: true };
    }
    const next = nextSocketEmitAttempt(attempt, maxAttempts);
    if (next == null) {
      return { emitted: true, attempts: attempt, acked: false };
    }
    attempt = next;
  }
}
